import * as Y from 'yjs';
import { makeVisualEdgeData } from '../schema/defaults';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../wiring/canvas-roots';

export interface ConnectionLike {
  source: string;
  target: string;
  sourceHandle: string | null;
  targetHandle: string | null;
}

/**
 * Rewrite the source handle to drop-{dir} so connector lines terminate
 * on the node edge midpoint (where the drop ring sits) rather than on
 * the floating expand dot. The visible exp-{dir} dot is purely cosmetic;
 * it's the source-side gesture target but not the path endpoint.
 */
function remapHandle(id: string | null | undefined): string | null {
  if (typeof id !== 'string') return null;
  if (id.startsWith('exp-')) return `drop-${id.slice(4)}`;
  return id;
}

/**
 * Generate a short, collision-resistant edge id. crypto.randomUUID() works in
 * the browser and node 18+; we still hash to keep the id compact.
 */
function newEdgeId(): string {
  // Math.random() is fine here — collision-recovery isn't load-bearing because
  // edge ids are namespaced under a single Y.Map and Y.Map.set is last-writer.
  // The probability of collision in any realistic canvas is ~0.
  const rnd = Math.random().toString(36).slice(2, 10);
  return `e_${Date.now().toString(36)}_${rnd}`;
}

/**
 * Writes a new visual edge to `doc.getMap('edges')` based on a React Flow
 * Connection. Returns the new edge id, or `null` if the connection is
 * invalid (missing endpoints or self-loop).
 *
 * Defaults match the design contract: kind='visual', smooth curve,
 * arrow end marker, no start marker, default stroke. The write happens
 * inside a `doc.transact(..., clientId)` so the per-user UndoManager
 * tracks the change.
 */
export function createEdgeFromConnection(
  doc: Y.Doc,
  clientId: number,
  createdBy: string,
  conn: ConnectionLike,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): string | null {
  if (!conn.source || !conn.target) return null;
  if (conn.source === conn.target) return null;

  const id = newEdgeId();
  const now = Date.now();

  doc.transact(() => {
    const { edges, zOrder } = canvasRoots(doc, binding);
    const edge = new Y.Map<unknown>();
    edge.set('id', id);
    edge.set('source', conn.source);
    edge.set('target', conn.target);
    // Persist source/target handles using the always-present drop-{dir}
    // handles. The visible expand-dots ('exp-{dir}') only mount when a node
    // is selected, so an edge anchored to one would fail to render whenever
    // the source/target wasn't currently selected. The drop handles are
    // mounted on every node always, so the edge stays drawable through
    // selection changes. We map exp-* → drop-* at write time.
    edge.set('sourceHandle', remapHandle(conn.sourceHandle));
    edge.set('targetHandle', remapHandle(conn.targetHandle));
    edge.set('kind', 'visual');
    edge.set('locked', false);
    edge.set('createdAt', now);
    edge.set('createdBy', createdBy);
    edge.set('updatedAt', now);

    const data = new Y.Map<unknown>();
    const defaults = makeVisualEdgeData();
    data.set('label', defaults.label);
    data.set('stroke', defaults.stroke);
    data.set('strokeWidth', defaults.strokeWidth);
    data.set('strokeStyle', defaults.strokeStyle);
    data.set('markerStart', defaults.markerStart);
    data.set('markerEnd', defaults.markerEnd);
    data.set('curvature', defaults.curvature);
    edge.set('data', data);

    edges.set(id, edge);
    // Push the new id onto the unified zOrder array. Nodes and edges
    // share one stack so users can re-order edges against shapes (Miro/
    // FigJam parity). Last entry renders on top.
    zOrder.push([id]);
  }, clientId);

  return id;
}
